import type { TFunction } from 'i18next';
import { useReducer } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { MFA_FACTOR } from '@repo/common';
import { DataTestIds } from '@repo/frontend-shared';
import { SDSButton } from '@repo/sds/SDSButton';
import { SDSFlexbox } from '@repo/sds/SDSFlexbox';
import { SDSText } from '@repo/sds/SDSText';
import { toast } from '@repo/sds/utils/toast';
import { Loader } from '@repo/ui/components/elements/Loader/Loader';
import { SettingsCard } from '@repo/ui/components/elements/settings-card/settings-card';
import { useCurrentUser } from '@repo/ui/contexts/current-user-context';
import { useDeviceType } from '@repo/ui/hooks/use-device-type';
import { openGoogleAccountReauthUrlFromLocation } from '@repo/ui/utils/open-auth-url';

import { formatMfaFactorDate } from './mfa-authenticator-manage-utils';
import {
  clearPrimaryReplaceIntent,
  hasReplaceSmsIntent,
  primaryMethodLabel,
  resolveLoginPrimaryFactor,
} from './mfa-method-picker-utils';
import { MfaSettingsDialogs } from './mfa-settings-dialogs';
import type { MfaSettingsTranslate } from './use-mfa-settings';
import { useMfaSettings } from './use-mfa-settings';

import './mfa-settings-section.scss';

const MFA = DataTestIds.COMMON.SECURITY.MFA;

function FactorActionButton({
  dataTestId,
  isFullWidth,
  isSubmitting,
  onClick,
  status,
  text,
}: {
  dataTestId: string;
  isFullWidth?: boolean;
  isSubmitting?: boolean;
  onClick: () => void;
  status?: 'alert';
  text: string;
}) {
  return (
    <SDSButton
      className="MfaSettingsSection__action"
      dataTestId={dataTestId}
      isFullWidth={isFullWidth}
      isSubmitting={isSubmitting}
      mode="outline"
      onClick={onClick}
      status={status}
      text={text}
    />
  );
}

type MfaSettingsApi = ReturnType<typeof useMfaSettings>;

/** The single landing-card CTA, chosen by which enrollment (if any) is already pending. */
function MfaLandingActionButton({
  pendingFactor,
  busy,
  requestAction,
  translate,
  t,
}: {
  pendingFactor: MfaSettingsApi['pendingFactor'];
  busy: boolean;
  requestAction: MfaSettingsApi['requestAction'];
  translate: MfaSettingsTranslate;
  t: TFunction<'common'>;
}) {
  if (pendingFactor === MFA_FACTOR.Email) {
    return (
      <FactorActionButton
        dataTestId={MFA.EMAIL_CONTINUE}
        isSubmitting={busy}
        onClick={() => requestAction({ method: MFA_FACTOR.Email, type: 'enroll' }, translate)}
        text={t('security.mfa.continueSetup')}
      />
    );
  }

  if (pendingFactor === MFA_FACTOR.Sms) {
    return (
      <FactorActionButton
        dataTestId={MFA.SMS_SEND}
        isSubmitting={busy}
        onClick={() => requestAction({ method: MFA_FACTOR.Sms, type: 'enroll' }, translate)}
        text={t('security.mfa.continueSetup')}
      />
    );
  }

  if (pendingFactor === MFA_FACTOR.Totp) {
    return (
      <FactorActionButton
        dataTestId={MFA.TOTP_CONTINUE}
        isSubmitting={busy}
        onClick={() => requestAction({ method: MFA_FACTOR.Totp, type: 'enroll' }, translate)}
        text={t('security.mfa.continueSetup')}
      />
    );
  }

  return (
    <FactorActionButton
      dataTestId={MFA.TOTP_ENROLL}
      isSubmitting={busy}
      onClick={() => requestAction({ type: 'choose-method' }, translate)}
      text={t('security.mfa.enroll')}
    />
  );
}

/** MFA disabled/pending state: title + status pill + the landing CTA. */
function MfaLandingCard({
  isMobile,
  pendingFactor,
  busy,
  requestAction,
  translate,
  t,
}: {
  isMobile: boolean;
  pendingFactor: MfaSettingsApi['pendingFactor'];
  busy: boolean;
  requestAction: MfaSettingsApi['requestAction'];
  translate: MfaSettingsTranslate;
  t: TFunction<'common'>;
}) {
  return (
    <SettingsCard direction={isMobile ? 'column' : 'row'} variant="outlined">
      <SDSFlexbox className="MfaSettingsSection__copy" direction="column" gap="text-small">
        <SDSFlexbox alignItems="center" gap="text-small">
          <SDSText text={t('security.mfa.title')} type="h5" />
          <span className="MfaSettingsSection__pill" data-testid={MFA.STATUS}>
            <SDSText
              color="muted"
              text={pendingFactor ? t('security.mfa.pending') : t('security.mfa.disabled')}
              type="body-small"
            />
          </span>
        </SDSFlexbox>
        <SDSText color="text-secondary" text={t('security.mfa.landingDescription')} type="body-medium" />
      </SDSFlexbox>
      <MfaLandingActionButton
        pendingFactor={pendingFactor}
        busy={busy}
        requestAction={requestAction}
        translate={translate}
        t={t}
      />
    </SettingsCard>
  );
}

/** MFA already enabled: title + status pill + primary method summary + manage CTA. */
function MfaEnabledCard({
  isMobile,
  enrolled,
  accountPrimary,
  requestAction,
  translate,
  t,
}: {
  isMobile: boolean;
  enrolled: MfaSettingsApi['enrolled'];
  accountPrimary: MfaSettingsApi['accountPrimary'];
  requestAction: MfaSettingsApi['requestAction'];
  translate: MfaSettingsTranslate;
  t: TFunction<'common'>;
}) {
  return (
    <SettingsCard direction={isMobile ? 'column' : 'row'} variant="outlined">
      <SDSFlexbox className="MfaSettingsSection__copy" direction="column" gap="text-small">
        <SDSFlexbox alignItems="center" gap="text-small">
          <SDSText text={t('security.mfa.title')} type="h5" />
          <span className="MfaSettingsSection__pill MfaSettingsSection__pill--on" data-testid={MFA.STATUS}>
            <SDSText color="primary" text={t('security.mfa.enabled')} type="body-small" />
          </span>
        </SDSFlexbox>
        <SDSText
          color="text-secondary"
          text={t('security.mfa.primaryMethod', {
            method: primaryMethodLabel(enrolled, t, accountPrimary),
          })}
          type="body-medium"
        />
      </SDSFlexbox>
      <FactorActionButton
        dataTestId={MFA.MANAGE}
        onClick={() => requestAction({ type: 'choose-method' }, translate)}
        text={t('security.mfa.manageTwoStep')}
      />
    </SettingsCard>
  );
}

/** Backup-codes card, shown only once MFA is enabled. */
function MfaBackupCard({
  enabled,
  isMobile,
  generatedOn,
  requestAction,
  translate,
  t,
}: {
  enabled: boolean;
  isMobile: boolean;
  generatedOn: string | undefined;
  requestAction: MfaSettingsApi['requestAction'];
  translate: MfaSettingsTranslate;
  t: TFunction<'common'>;
}) {
  if (!enabled) {
    return null;
  }

  return (
    <SettingsCard direction={isMobile ? 'column' : 'row'} variant="outlined">
      <SDSFlexbox className="MfaSettingsSection__copy" direction="column" gap="text-small">
        <SDSText text={t('security.mfa.backupCardTitle')} type="h5" />
        {generatedOn ? (
          <SDSText
            color="text-secondary"
            dataTestId={MFA.REMAINING_BACKUP}
            text={t('security.mfa.backupGenerated', { date: generatedOn })}
            type="body-medium"
          />
        ) : null}
      </SDSFlexbox>
      <FactorActionButton
        dataTestId={MFA.REGENERATE}
        onClick={() => requestAction({ type: 'regenerate' }, translate)}
        text={t('security.mfa.backupGenerateNew')}
      />
    </SettingsCard>
  );
}

export function MfaSettingsSection() {
  const { currentUser } = useCurrentUser();
  if (!currentUser) return <Loader />;

  return <AuthenticatedMfaSettingsSection key={currentUser.id} currentUser={currentUser} />;
}

interface MfaDialogInputState {
  dialog: MfaSettingsApi['dialog'];
  password: string;
  code: string;
  existingFactor: string;
  existingFactorCode: string;
}

type MfaDialogInputAction =
  | { type: 'field'; field: 'password' | 'code' | 'existingFactor' | 'existingFactorCode'; value: string }
  | { type: 'dialog'; dialog: MfaSettingsApi['dialog'] }
  | { type: 'clear'; existingFactor: string };

function dialogInputReducer(state: MfaDialogInputState, action: MfaDialogInputAction): MfaDialogInputState {
  if (action.type === 'field') return { ...state, [action.field]: action.value };
  if (action.type === 'clear')
    return { ...state, password: '', code: '', existingFactorCode: '', existingFactor: action.existingFactor };
  const kind = action.dialog.kind;
  const switchingSetup = kind !== state.dialog.kind && ['totp-setup', 'email-setup', 'sms-setup'].includes(kind);
  return {
    ...state,
    dialog: action.dialog,
    code: switchingSetup ? '' : state.code,
    existingFactor: action.dialog.kind === 'existing-factor' ? action.dialog.existingFactor : state.existingFactor,
  };
}

function AuthenticatedMfaSettingsSection({
  currentUser,
}: {
  currentUser: NonNullable<ReturnType<typeof useCurrentUser>['currentUser']>;
}) {
  const { t } = useTranslation('common');
  const { isMobile, isTablet } = useDeviceType();
  const location = useLocation();
  const { hasPassword, fullName } = currentUser;
  const settings = useMfaSettings();
  const [inputs, dispatchInput] = useReducer(dialogInputReducer, {
    dialog: settings.dialog,
    password: '',
    code: '',
    existingFactor: MFA_FACTOR.Totp,
    existingFactorCode: '',
  });
  if (inputs.dialog !== settings.dialog) dispatchInput({ type: 'dialog', dialog: settings.dialog });
  const { password, code, existingFactor, existingFactorCode } = inputs;
  const setPassword = (value: string) => dispatchInput({ type: 'field', field: 'password', value });
  const setCode = (value: string) => dispatchInput({ type: 'field', field: 'code', value });
  const setExistingFactor = (value: string) => dispatchInput({ type: 'field', field: 'existingFactor', value });
  const setExistingFactorCode = (value: string) => dispatchInput({ type: 'field', field: 'existingFactorCode', value });

  const translate = (key: string, options?: { method?: string; name?: string; seconds?: number }) =>
    t(`security.mfa.${key}`, { ...options, defaultValue: key });
  const generatedOn = formatMfaFactorDate(settings.backupGeneratedAt);
  const showLanding = !settings.enabled;

  const proofFactor = () => resolveLoginPrimaryFactor(settings.enrolled, settings.accountPrimary);

  const closeDialog = () => {
    settings.clearSecrets();
    dispatchInput({ type: 'clear', existingFactor: proofFactor() });
  };

  if (settings.loading) {
    return (
      <SettingsCard dataTestId={MFA.LOADING} direction="column" variant="outlined">
        <Loader />
        <SDSText color="text-secondary" text={t('security.mfa.loading')} type="body-medium" />
      </SettingsCard>
    );
  }

  if (settings.loadError) {
    return (
      <SettingsCard dataTestId={MFA.SECTION} direction="column" variant="outlined">
        <SDSText color="alert" dataTestId={MFA.ERROR} text={t('security.mfa.loadError')} type="body-medium" />
      </SettingsCard>
    );
  }

  return (
    <SDSFlexbox
      className={
        isMobile ? 'MfaSettingsSection MfaSettingsSection--mobile' : 'MfaSettingsSection MfaSettingsSection--desktop'
      }
      dataTestId={MFA.SECTION}
      direction="column"
      gap="content-default"
    >
      {showLanding ? (
        <MfaLandingCard
          isMobile={isMobile}
          pendingFactor={settings.pendingFactor}
          busy={settings.busy}
          requestAction={settings.requestAction}
          translate={translate}
          t={t}
        />
      ) : (
        <MfaEnabledCard
          isMobile={isMobile}
          enrolled={settings.enrolled}
          accountPrimary={settings.accountPrimary}
          requestAction={settings.requestAction}
          translate={translate}
          t={t}
        />
      )}

      <MfaBackupCard
        enabled={settings.enabled}
        isMobile={isMobile}
        generatedOn={generatedOn}
        requestAction={settings.requestAction}
        translate={translate}
        t={t}
      />

      <MfaSettingsDialogs
        accountPrimary={settings.accountPrimary}
        actionError={settings.actionError}
        busy={settings.busy}
        code={code}
        cooldownSeconds={settings.cooldownSeconds}
        dialog={settings.dialog}
        existingFactor={existingFactor}
        existingFactorCode={existingFactorCode}
        enrolled={settings.enrolled}
        totpEnrolledAt={settings.totpEnrolledAt}
        totpLastUsedAt={settings.totpLastUsedAt}
        totpAppName={settings.status?.totpAppName}
        emailEnrolledAt={settings.emailEnrolledAt}
        emailLastUsedAt={settings.emailLastUsedAt}
        smsEnrolledAt={settings.smsEnrolledAt}
        smsLastUsedAt={settings.smsLastUsedAt}
        smsPhoneMasked={settings.smsPhoneMasked}
        hasPassword={hasPassword}
        isMobile={isMobile}
        isTablet={isTablet}
        oauthProfile={{
          avatarSrc: currentUser.picture?.thumbnailUrl,
          continueAsName: currentUser.firstName || fullName,
          email: currentUser.email,
          name: fullName,
        }}
        passkeyCredentials={settings.passkeyCredentials}
        onAddEnrolledFactor={method => {
          void settings.enrollFactor(method, translate);
        }}
        onAccountPrimaryChange={method => void settings.applyAccountPrimary(method, translate)}
        onBackToMethods={() => {
          setCode('');
          clearPrimaryReplaceIntent();
          settings.setDialog({ kind: hasReplaceSmsIntent() ? 'sms-add-method' : 'choose-method' });
        }}
        onCancel={closeDialog}
        onChooseMethod={method => void settings.chooseMethod(method, translate)}
        onClearActionError={() => settings.setActionError(undefined)}
        onChangeEmail={() => {
          void settings.enrollFactor(MFA_FACTOR.Email, translate);
        }}
        onConfirmRemoveEmail={replacement => {
          void settings.confirmPrimaryRemove('email', replacement, translate);
        }}
        onConfirmRemovePasskey={replacement => {
          void settings.confirmPrimaryRemove('passkey', replacement, translate);
        }}
        onConfirmRemoveSms={replacement => {
          void settings.confirmPrimaryRemove(MFA_FACTOR.Sms, replacement, translate);
        }}
        onConfirmRemoveTotp={replacement => {
          void settings.confirmPrimaryRemove('totp', replacement, translate);
        }}
        onConfirmSetup={() => void settings.confirmSetup(code, translate)}
        onConfirmPasskeyRegistration={async responseJson => {
          try {
            return await settings.confirmPasskeyRegistration(responseJson);
          } catch {
            return false;
          }
        }}
        onConfirmDisableAll={() => {
          void settings.confirmDisable({ type: 'disable' }, translate);
        }}
        onDisable={() => settings.requestAction({ type: 'disable' }, translate)}
        onExistingFactorChange={setExistingFactor}
        onExistingFactorCodeChange={setExistingFactorCode}
        onFinishPrimaryReplace={() => settings.finishPrimaryReplace(translate)}
        onManageTotp={() => settings.setDialog({ kind: 'totp-manage' })}
        onManageEmail={() => settings.setDialog({ kind: 'email-manage' })}
        onManagePasskey={() => settings.setDialog({ kind: 'passkey-manage' })}
        onManageSms={() => settings.setDialog({ kind: 'sms-manage' })}
        onAddAnotherPasskey={() => {
          setCode('');
          void settings.enrollFactor(MFA_FACTOR.Passkey, translate, { addAnotherPasskey: true });
        }}
        onChangeSmsPhone={() => {
          setCode('');
          settings.setDialog({ kind: 'sms-setup', changePhone: true });
        }}
        onRemoveEmail={() => settings.setDialog({ kind: 'email-remove' })}
        onRemovePasskey={() => settings.setDialog({ kind: 'passkey-remove' })}
        onRemovePasskeyCredential={credentialId => settings.removePasskeyCredential(credentialId)}
        onRemovePasskeys={() => settings.setDialog({ kind: 'passkey-remove' })}
        onRemoveSms={() => settings.setDialog({ kind: 'sms-remove' })}
        onOauthContinue={() => {
          settings.rememberOauthReauthResume();
          void openGoogleAccountReauthUrlFromLocation(location).catch(error => {
            toast.error(error instanceof Error ? error.message : 'Google verification could not be started');
          });
        }}
        onOpenPasskeySetup={() => {
          setCode('');
          void settings.enrollFactor(MFA_FACTOR.Passkey, translate);
        }}
        onOpenSmsSetup={() => {
          setCode('');
          settings.setDialog({ kind: 'sms-setup' });
        }}
        onPasswordChange={setPassword}
        onRemoveTotp={() => settings.setDialog({ kind: 'totp-remove' })}
        onReplaceTotp={() => {
          void settings.enrollFactor(MFA_FACTOR.Totp, translate);
        }}
        onResendEmail={() => settings.resendEmail(translate)}
        onSendSmsCode={phoneE164 => settings.sendSmsEnrollmentCode(phoneE164, translate)}
        onSaveTotpAppName={name => void settings.applyTotpAppName(name, translate)}
        onSetupCodeChange={setCode}
        onSubmitExistingFactor={() => void settings.submitExistingFactor(existingFactor, existingFactorCode, translate)}
        onSubmitReauth={() => void settings.submitReauth(hasPassword ? password : undefined, translate)}
        password={password}
        t={t}
      />
    </SDSFlexbox>
  );
}
